import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import React from 'react';

import {SchemaTag, TargetFile, UnaffiliatedSchemaTag} from 'folder-items/FileConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import BioentityLinkTagSearchDropdown from 'metadata/BioentityLinkTagSearchDropdown';
import PartSearchDropdown, {PartSearchDropdownHandle} from 'metadata/PartSearchDropdown';
import {SUMMARIZE_THRESHOLD_DEFAULT} from 'metadata/TagValueViewer';
import ValidEntitiesForFieldPathDropdown from 'metadata/ValidEntitiesForFieldPathDropdown';
import {
  FIELD_TYPES,
  getRequiredLinkStaticSchemaInterface,
  getRequiredLinkTagSchema,
} from 'schemas/fields/SchemaFieldConstants';
import {ValidEntitiesForFieldPathQuery} from 'schemas/tags/__generated/ValidEntitiesForFieldPathQuery';
import {BaseBioentityLinkInputProps} from 'schemas/tags/BioentityLinkInputConstants';
import ControlledSearchInput from 'schemas/tags/ControlledSearchInput';
import * as TagUtil from 'schemas/tags/TagUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CustomAttachDropdownFromOptionData} from 'ui-library/attach-dropdown/AttachDropdownOption';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import SummaryChip from 'ui-library/chips/SummaryChip';
import * as logging from 'util/Logging';

type ValidEntityGraphQL = NonNullable<
  NonNullable<
    ExtractByTypename<
      ValidEntitiesForFieldPathQuery['bioentity'],
      'BasicFolderItemNode'
    >['validEntitiesForFieldPathInfo']
  >['validEntities']
>[number];

export type ControlledBioentityLinkInputProps<T extends SchemaTag | UnaffiliatedSchemaTag> = Omit<
  BaseBioentityLinkInputProps<T>,
  'chipSize' | 'placeholder' | 'summarize'
> & {
  chipSize?: ChipSize;
  placeholder?: string;
  summarize?: boolean;
  inputValue: string;
  onUpdateInputValue: (inputValue: string) => void;
  validEntities?: Array<ValidEntityGraphQL>;
  loadingValidEntities?: boolean;
  summarizeThreshold?: number;
};

interface BioentityChipsProps {
  items: Array<TargetFile>;
  summarize: boolean;
  summarizeThreshold: number;
  chipSize: ChipSize;
  onRemoveItem: (index: number) => void;
  onRemoveAll: () => void;
  areChipsClickable?: boolean;
}

function BioentityChips({
  summarize,
  summarizeThreshold,
  items,
  chipSize,
  onRemoveItem,
  onRemoveAll,
  areChipsClickable,
}: BioentityChipsProps): JSX.Element | null {
  if (items.length === 0) {
    return null;
  }
  if (summarize && items.length >= summarizeThreshold) {
    return (
      <SummaryChip
        size={chipSize}
        itemSummarizer={(targetFile) => targetFile.name}
        itemLabel={FIELD_TYPES.BIOENTITY_LINK.label}
        items={items}
        onRemove={onRemoveAll}
      />
    );
  } else {
    return (
      <>
        {items.map((file, index) => (
          <BioentityChip
            className="chip-container"
            id={file.id}
            isLink={Boolean(areChipsClickable)}
            isInaccessible={!file.id}
            key={index}
            size={chipSize}
            onRemove={() => {
              onRemoveItem(index);
            }}
          />
        ))}
      </>
    );
  }
}

export default function ControlledBioentityLinkInput({
  autoFocus = true,
  areChipsClickable,
  chipSize = ChipSize.SMALL,
  className,
  inputValue,
  isDisabled = false,
  isOutsideTable,
  loadingValidEntities = false,
  noResultsOptions,
  onBlur,
  onChange,
  onKeyDown,
  onUpdateInputValue,
  placeholder = '',
  sequenceParts,
  shouldAllowMultiple = false,
  summarize = false,
  summarizeThreshold = SUMMARIZE_THRESHOLD_DEFAULT,
  tag,
  validEntities,
}: ControlledBioentityLinkInputProps<SchemaTag | UnaffiliatedSchemaTag>): JSX.Element {
  function _getTargetFiles(): Array<TargetFile> {
    return tag.targetFiles || [];
  }

  function _canSelectAdditional(): boolean {
    const {tagSchemaField} = tag;
    const targetFiles = _getTargetFiles();
    return targetFiles.length === 0 || tagSchemaField.isMulti || Boolean(shouldAllowMultiple);
  }

  function _getLinksAreDisplayed(): boolean {
    return _getTargetFiles().length > 0;
  }

  function _removeLink(index: number): void {
    const targetFiles = reactUpdate(tag.targetFiles, {$splice: [[index, 1]]});
    onChange({
      targetFiles,
      value: '',
    });
  }

  function _removeAllLinks(): void {
    onChange({
      targetFiles: [],
      value: '',
    });
  }

  function _handleUpdateInputValue(inputValue: string): void {
    onUpdateInputValue(inputValue);
    onChange({
      targetFiles: tag.targetFiles,
      value: '',
    });
  }

  function _handleSelectAttachment(id: string, name: string, url: string): void {
    const targetFiles = reactUpdate(_getTargetFiles(), {$push: [{id, name, url}]});
    onUpdateInputValue('');
    onChange({
      targetFiles,
      value: '',
    });
  }

  function _renderBioentityLinkDropdown(ref: React.RefObject<BioentityLinkTagSearchDropdown>): JSX.Element {
    if (validEntities || loadingValidEntities) {
      return (
        <ValidEntitiesForFieldPathDropdown
          validEntities={validEntities}
          query={inputValue}
          onSelectOption={_handleSelectAttachment}
        />
      );
    }
    return (
      <BioentityLinkTagSearchDropdown
        ref={ref}
        query={inputValue}
        selectAttachment={_handleSelectAttachment}
        requiredLink={tag.tagSchemaField.requiredLink!}
        noResultsItems={noResultsOptions?.map(({option, onClick}) => ({
          menuItem: <CustomAttachDropdownFromOptionData optionData={option} />,
          onClick,
        }))}
      />
    );
  }

  function _renderSearchDropdown(
    ref: React.RefObject<BioentityLinkTagSearchDropdown | PartSearchDropdownHandle>
  ): JSX.Element | null {
    if (!sequenceParts) {
      // If no parts are given, search all sequences and allow any sequence of the right schema to be
      // selected
      return _renderBioentityLinkDropdown(ref as React.RefObject<BioentityLinkTagSearchDropdown>);
    }
    const {tagSchemaField} = tag;
    const fieldType = TagUtil.getFullFieldType(tagSchemaField.fieldType);
    const linkedSchemaSummary =
      getRequiredLinkTagSchema(tagSchemaField) || getRequiredLinkStaticSchemaInterface(tagSchemaField);
    switch (fieldType.type) {
      case FIELD_TYPES.BIOENTITY_LINK.type:
      case FIELD_TYPES.TRANSCRIPTION_LINK.type:
      case FIELD_TYPES.TRANSLATION_LINK.type:
      case FIELD_TYPES.AA_PART_LINK.type:
      case FIELD_TYPES.BASE_MOLECULE_LINK.type:
        return _renderBioentityLinkDropdown(ref as React.RefObject<BioentityLinkTagSearchDropdown>);
      case FIELD_TYPES.PART_LINK.type:
        return (
          <PartSearchDropdown
            ref={ref as React.RefObject<PartSearchDropdownHandle>}
            query={inputValue}
            selectAttachment={_handleSelectAttachment}
            sequenceParts={sequenceParts}
            linkedSchemaOrInterfaceId={linkedSchemaSummary ? linkedSchemaSummary.id : null}
          />
        );
      default:
        logging.error(`Unexpected fieldType in BioentityLinkInput: ${fieldType}`);
        return null;
    }
  }

  const fieldType = TagUtil.getFullFieldType(tag.tagSchemaField.fieldType).type;
  const shouldForwardKeydown = fieldType === FIELD_TYPES.PART_LINK.type || Boolean(inputValue);

  return (
    <div
      className={classNames(className, 'bioentityLinkInput')}
      {...getTestProps({
        component: 'ControlledBioentityLinkInput',
        element: 'root',
      })}
    >
      <ControlledSearchInput
        isDisabled={isDisabled}
        shouldForwardKeydown={shouldForwardKeydown}
        canSelectAdditional={_canSelectAdditional()}
        hasTypedValue={_getLinksAreDisplayed()}
        onRemoveLastValue={() => {
          _removeLink(_getTargetFiles().length - 1);
        }}
        autoFocus={Boolean(autoFocus)}
        inputValue={inputValue}
        onUpdateInputValue={_handleUpdateInputValue}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        renderSelectedValues={() => (
          <BioentityChips
            items={_getTargetFiles()}
            // Inside an assay run with automation input file configs, we summarize the links
            summarize={summarize}
            summarizeThreshold={summarizeThreshold}
            chipSize={chipSize}
            onRemoveItem={_removeLink}
            onRemoveAll={_removeAllLinks}
            areChipsClickable={areChipsClickable}
          />
        )}
        renderSearchDropdown={_renderSearchDropdown}
        shouldCloseOnInsideClick={true}
        isOutsideTable={isOutsideTable}
        placeholder={_canSelectAdditional() ? placeholder : ''}
      />
    </div>
  );
}
